// Tipos de estado de pedido
export type OrderStatus =
  | 'por_confirmar'
  | 'por_preparar'
  | 'en_preparacion'
  | 'preparado'
  | 'enviado'
  | 'devuelto'
  | 'cancelado';

// Configuración de botones del toolbar de estados
export interface ToolbarButton {
  key: string;
  link: string;
  status_id?: number;
}

// Configuración de acciones disponibles por estado
export interface StatusActions {
  cancel: boolean;
  prepare: boolean;
  markForSend: boolean;
  uncancel: boolean;
  view: boolean;
  updateFromStore: boolean;
  duplicate: boolean;
}

// Configuración de acciones del dropdown
export interface DropdownAction {
  id: string;
  icon: any;
  label: string;
  action: string;
  requiresConfirmation: boolean;
  confirmationKey?: string;
}

/** Estados en los que la acción 3PL «Refrescar estado logístico» tiene sentido (envío / seguimiento). */
export const ORDER_STATUSES_ELIGIBLE_FOR_REFRESH_LOGISTICS: OrderStatus[] = ['enviado', 'devuelto'];

export type RefreshLogisticsInsertMode = 'after_view_order' | 'prepend';

/** Inserta la acción «Ver adjuntos» tras «Ver pedido» cuando corresponde por estado/adjuntos. */
export function injectAttachmentsDropdownAction(
  actions: DropdownAction[],
  options: {
    enabled: boolean;
    t: (key: string) => string;
    icon: DropdownAction['icon'];
    insertMode?: RefreshLogisticsInsertMode;
  },
): DropdownAction[] {
  const { enabled, t, icon, insertMode = 'after_view_order' } = options;
  if (!enabled) {
    return actions;
  }

  const attachmentsAction: DropdownAction = {
    id: 'view_attachments',
    icon,
    label: t('actions.view_attachments'),
    action: 'view_attachments',
    requiresConfirmation: false,
  };

  if (insertMode === 'prepend') {
    return [attachmentsAction, ...actions];
  }

  const idx = actions.findIndex((a) => a.action === 'view_order');
  if (idx === -1) {
    return [attachmentsAction, ...actions];
  }

  return [...actions.slice(0, idx + 1), attachmentsAction, ...actions.slice(idx + 1)];
}

/** Inserta retener/liberar hold en pedidos `por_preparar` (requiere `orders.manage-held`). */
export function injectHoldForPreparationDropdownAction(
  actions: DropdownAction[],
  options: {
    enabled: boolean;
    status: OrderStatus;
    held: boolean;
    t: (key: string) => string;
    holdIcon: DropdownAction['icon'];
    releaseIcon: DropdownAction['icon'];
  },
): DropdownAction[] {
  const { enabled, status, held, t, holdIcon, releaseIcon } = options;
  if (!enabled || status !== 'por_preparar') {
    return actions;
  }

  const holdAction: DropdownAction = held
    ? {
        id: 'release_hold',
        icon: releaseIcon,
        label: t('actions.release_hold'),
        action: 'release_hold',
        requiresConfirmation: true,
        confirmationKey: 'releaseHold',
      }
    : {
        id: 'hold_for_preparation',
        icon: holdIcon,
        label: t('actions.hold_for_preparation'),
        action: 'hold_for_preparation',
        requiresConfirmation: true,
        confirmationKey: 'holdForPreparation',
      };

  const idx = actions.findIndex((a) => a.action === 'view_order');
  if (idx === -1) {
    return [holdAction, ...actions];
  }

  return [...actions.slice(0, idx + 1), holdAction, ...actions.slice(idx + 1)];
}

/** Inserta la acción de refresco desde courier (solo debe usarse cuando `enabled` ya refleja tenant 3PL). */
export function injectRefreshLogisticsDropdownAction(
  actions: DropdownAction[],
  options: {
    enabled: boolean;
    status: OrderStatus;
    t: (key: string) => string;
    icon: DropdownAction['icon'];
    insertMode: RefreshLogisticsInsertMode;
  },
): DropdownAction[] {
  const { enabled, status, t, icon, insertMode } = options;
  if (!enabled || !ORDER_STATUSES_ELIGIBLE_FOR_REFRESH_LOGISTICS.includes(status)) {
    return actions;
  }
  const refreshAction: DropdownAction = {
    id: 'refresh_logistics',
    icon,
    label: t('actions.refresh_logistics'),
    action: 'refresh_logistics',
    requiresConfirmation: false,
  };
  if (insertMode === 'prepend') {
    return [refreshAction, ...actions];
  }
  const idx = actions.findIndex((a) => a.action === 'view_order');
  if (idx === -1) {
    return [refreshAction, ...actions];
  }
  return [...actions.slice(0, idx + 1), refreshAction, ...actions.slice(idx + 1)];
}

// Mapeo de códigos de estado a estados del dropdown
export const getStatusForDropdown = (statusCode: number): OrderStatus => {
  switch (statusCode) {
    case 1:
      return 'por_confirmar';
    case 2:
    case 3:
      return 'por_preparar';
    case 4:
    case 5:
      return 'en_preparacion';
    case 6:
      return 'preparado';
    case 7:
      return 'enviado';
    case 8:
      return 'devuelto';
    case 9:
      return 'cancelado';
    default:
      return 'por_confirmar';
  }
};

// Configuración de acciones disponibles por estado
export const getAvailableActionsByStatus = (status: OrderStatus): StatusActions => {
  switch (status) {
    case 'por_confirmar':
      return {
        cancel: true,
        prepare: false,
        markForSend: true,
        uncancel: false,
        view: true,
        updateFromStore: false,
        duplicate: false,
      };
    case 'por_preparar':
      return {
        cancel: true,
        prepare: false,
        markForSend: false,
        uncancel: false,
        view: true,
        updateFromStore: false,
        duplicate: false,
      };
    case 'en_preparacion':
      return {
        cancel: false,
        prepare: false,
        markForSend: false,
        uncancel: false,
        view: true,
        updateFromStore: false,
        duplicate: false,
      };
    case 'preparado':
      return {
        cancel: false,
        prepare: false,
        markForSend: false,
        uncancel: false,
        view: true,
        updateFromStore: false,
        duplicate: false,
      };
    case 'enviado':
      return {
        cancel: false,
        prepare: false,
        markForSend: false,
        uncancel: false,
        view: true,
        updateFromStore: false,
        duplicate: false,
      };
    case 'devuelto':
      return {
        cancel: false,
        prepare: false,
        markForSend: false,
        uncancel: false,
        view: true,
        updateFromStore: false,
        duplicate: false,
      };
    case 'cancelado':
      return {
        cancel: false,
        prepare: false,
        markForSend: false,
        uncancel: true,
        view: true,
        updateFromStore: false,
        duplicate: false,
      };
    default:
      return {
        cancel: false,
        prepare: false,
        markForSend: false,
        uncancel: false,
        view: true,
        updateFromStore: false,
        duplicate: false,
      };
  }
};

// Configuración de botones del toolbar
export const toolbarButtons: ToolbarButton[] = [
  { key: 'all', link: '#' },
  { key: 'to_confirm', link: '#', status_id: 1 },
  { key: 'pending', link: '#', status_id: 2 },
  { key: 'to_prepare', link: '#', status_id: 3 },
  { key: 'in_preparation', link: '#', status_id: 4 }, // Picking
  { key: 'prepared', link: '#', status_id: 6 },
  { key: 'sent', link: '#', status_id: 7 },
  { key: 'returned', link: '#', status_id: 8 },
  { key: 'cancelled', link: '#', status_id: 9 },
];

/** Clave i18n `toolbar.*` del listado para un status_id; null si no hay mapeo (SAAS-3936). */
export function getOrderStatusToolbarKey(statusId?: number | null): string | null {
  if (statusId == null) {
    return null;
  }
  const button = toolbarButtons.find((btn) => btn.status_id === statusId);
  return button?.key ?? null;
}

// Configuración de acciones del dropdown por estado
export const getDropdownActionsByStatus = (
  status: OrderStatus,
  t: (key: string) => string,
  icons: {
    faCircleXmark: any;
    faArrowsRotate: any;
    faCircleCheck: any;
    faCopy: any;
    faSquareMinus: any;
    faEye: any;
    faTag: any;
    faPrint: any;
    faXmark: any;
  },
  hasUnassignStockPermission: boolean = true,
): DropdownAction[] => {
  switch (status) {
    case 'por_confirmar':
      return [
        {
          id: 'view_order',
          icon: icons.faEye,
          label: t('actions.view_order'),
          action: 'view_order',
          requiresConfirmation: true,
        },
        {
          id: 'cancel',
          icon: icons.faCircleXmark,
          label: t('actions.cancel'),
          action: 'cancel',
          requiresConfirmation: true,
          confirmationKey: 'cancel',
        },
        {
          id: 'mark_send',
          icon: icons.faCircleCheck,
          label: t('actions.mark_for_send'),
          action: 'mark_send',
          requiresConfirmation: true,
          confirmationKey: 'markForSend',
        },
        {
          id: 'update_store',
          icon: icons.faArrowsRotate,
          label: t('actions.update_from_store'),
          action: 'update_store',
          requiresConfirmation: false,
        },
        {
          id: 'duplicate',
          icon: icons.faCopy,
          label: t('actions.duplicate'),
          action: 'duplicate',
          requiresConfirmation: true,
        },
      ];

    case 'por_preparar': {
      const porPrepararActions = [
        {
          id: 'view_order',
          icon: icons.faEye,
          label: t('actions.view_order'),
          action: 'view_order',
          requiresConfirmation: true,
        },
        {
          id: 'cancel',
          icon: icons.faCircleXmark,
          label: t('actions.cancel'),
          action: 'cancel',
          requiresConfirmation: true,
          confirmationKey: 'cancel',
        },
      ];

      // Solo agregar la acción de desasignar stock si el usuario tiene permisos
      if (hasUnassignStockPermission) {
        porPrepararActions.push({
          id: 'unassign_stock',
          icon: icons.faSquareMinus,
          label: t('actions.unassign_stock'),
          action: 'unassign_stock',
          requiresConfirmation: true,
          confirmationKey: 'unassignStock',
        });
      }

      porPrepararActions.push(
        {
          id: 'update_store',
          icon: icons.faArrowsRotate,
          label: t('actions.update_from_store'),
          action: 'update_store',
          requiresConfirmation: false,
        },
        {
          id: 'labels',
          icon: icons.faTag,
          label: t('actions.labels'),
          action: 'labels',
          requiresConfirmation: true,
        },
        {
          id: 'duplicate',
          icon: icons.faCopy,
          label: t('actions.duplicate'),
          action: 'duplicate',
          requiresConfirmation: true,
        },
      );

      return porPrepararActions;
    }

    case 'en_preparacion':
      return [
        {
          id: 'view_order',
          icon: icons.faEye,
          label: t('actions.view_order'),
          action: 'view_order',
          requiresConfirmation: true,
        },
        {
          id: 'labels',
          icon: icons.faTag,
          label: t('actions.labels'),
          action: 'labels',
          requiresConfirmation: true,
        },
        {
          id: 'duplicate',
          icon: icons.faCopy,
          label: t('actions.duplicate'),
          action: 'duplicate',
          requiresConfirmation: true,
        },
      ];

    case 'preparado':
      return [
        {
          id: 'view_order',
          icon: icons.faEye,
          label: t('actions.view_order'),
          action: 'view_order',
          requiresConfirmation: true,
        },
        {
          id: 'labels',
          icon: icons.faTag,
          label: t('actions.labels'),
          action: 'labels',
          requiresConfirmation: true,
        },
        {
          id: 'duplicate',
          icon: icons.faCopy,
          label: t('actions.duplicate'),
          action: 'duplicate',
          requiresConfirmation: true,
        },
      ];

    case 'enviado':
      return [
        {
          id: 'view_order',
          icon: icons.faEye,
          label: t('actions.view_order'),
          action: 'view_order',
          requiresConfirmation: true,
        },
        {
          id: 'labels',
          icon: icons.faTag,
          label: t('actions.labels'),
          action: 'labels',
          requiresConfirmation: true,
        },
        {
          id: 'duplicate',
          icon: icons.faCopy,
          label: t('actions.duplicate'),
          action: 'duplicate',
          requiresConfirmation: true,
        },
      ];

    case 'devuelto':
      return [
        {
          id: 'view_order',
          icon: icons.faEye,
          label: t('actions.view_order'),
          action: 'view_order',
          requiresConfirmation: true,
        },
        {
          id: 'labels',
          icon: icons.faTag,
          label: t('actions.labels'),
          action: 'labels',
          requiresConfirmation: true,
        },
        {
          id: 'duplicate',
          icon: icons.faCopy,
          label: t('actions.duplicate'),
          action: 'duplicate',
          requiresConfirmation: true,
        },
      ];

    case 'cancelado':
      return [
        {
          id: 'view_order',
          icon: icons.faEye,
          label: t('actions.view_order'),
          action: 'view_order',
          requiresConfirmation: true,
        },
        {
          id: 'uncancel',
          icon: icons.faXmark,
          label: t('actions.uncancel'),
          action: 'uncancel',
          requiresConfirmation: true,
          confirmationKey: 'uncancel',
        },
        {
          id: 'duplicate',
          icon: icons.faCopy,
          label: t('actions.duplicate'),
          action: 'duplicate',
          requiresConfirmation: true,
        },
      ];

    default:
      return [
        {
          id: 'view_order',
          icon: icons.faEye,
          label: t('actions.view_order'),
          action: 'view_order',
          requiresConfirmation: true,
        },
      ];
  }
};

/** Color del punto por estado de pedido (fondo y texto del badge van unificados en la UI). */
export type StatusDotStyle = { dot: string };

export const defaultOrderStatusDot: StatusDotStyle = { dot: 'bg-muted-foreground' };
export const defaultLogisticStatusDot: StatusDotStyle = { dot: 'bg-muted-foreground' };

export const statusColorMap: Record<number, StatusDotStyle> = {
  // 1 y 2: tonos antes muy parecidos (badge-status-2 vs 1); naranja vs violeta para distinguirlos
  1: { dot: 'bg-orange-500' }, // Por confirmar
  2: { dot: 'bg-violet-600' }, // Pendiente
  3: { dot: 'bg-badge-status-3' },
  4: { dot: 'bg-bright-warning' },
  5: { dot: 'bg-info' },
  6: { dot: 'bg-teal-500' },
  7: { dot: 'bg-green-500' },
  8: { dot: 'bg-muted-foreground' },
  9: { dot: 'bg-bright-error' },
};

export const logisticStatusColorMap: Record<number, StatusDotStyle> = {
  1: { dot: 'bg-muted-foreground' },
  2: { dot: 'bg-bright-warning' },
  3: { dot: 'bg-info' },
  4: { dot: 'bg-success' },
  5: { dot: 'bg-bright-success' },
  6: { dot: 'bg-warning' },
  7: { dot: 'bg-error' },
  8: { dot: 'bg-bright-error' },
  9: { dot: 'bg-amber-500' },
  10: { dot: 'bg-foreground/70' },
};

// Configuración de iconos para las acciones de selección
export const getSelectionActionIcons = (icons: {
  faXmarkCircle: any;
  faBoxOpen: any;
  faCircleCheck: any;
  faXmark: any;
}) => ({
  cancel: icons.faXmarkCircle,
  prepare: icons.faBoxOpen,
  markForSend: icons.faCircleCheck,
  uncancel: icons.faXmark,
});
